import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  KeyboardEvent,
  useMemo,
  useLayoutEffect,
} from "react";
import { DialogHeader } from "./DialogHeader";
import { DialogFooter } from "./DialogFooter";
import { DialogContent } from "./DialogContent";
import { DialogCancelButton } from "./DialogCancelButton";
import {
  OpenCloseTransitionStateEffects,
  useDialogScrollLock,
  useDialogTransitionStates,
  useKeyboardFocusables,
  useOnClickOutside,
} from "../../internal/hooks";
import {
  FocusableElement,
  DisableCloseOnEscapeOrClickProps,
  Size,
} from "../../types";
import { registerLayer, unregisterLayer } from "../../internal/utils";
import { DialogContext, DialogContextProps } from "./internal/DialogContext";
import {
  DrillDownContext,
  DrillDownContextProps,
} from "../DrillDown/internal/DrillDownContext";
import { useDrillDownContextState } from "../DrillDown/internal/useDrillDownContextState";
import styles from "./Dialog.module.scss";
import drillDownStyles from "../DrillDown/DrillDown.module.scss";
import { useMergeRefs } from "../../hooks";
import cx from "classnames";

import { ToastProvider } from "../Toast/internal/ToastProvider";
import { ToasterPopover } from "../Toast/internal/ToasterPopover";
import { ToasterContent } from "../Toast/internal/Toaster";
import { useInitialFocus } from "./internal/useInitialFocus";

/**
 * Props for the Dialog component
 * @extends ComponentPropsWithoutRef<"dialog">
 * @extends DialogTransitionStateEffects
 * @extends DisableCloseOnEscapeOrClickProps
 */
export type DialogProps = ComponentPropsWithoutRef<"dialog"> &
  OpenCloseTransitionStateEffects &
  DisableCloseOnEscapeOrClickProps & {
    /**
     * @default false
     */
    fullScreen?: boolean;
    /**
     * @default "medium"
     */
    size?: Extract<Size, "medium" | "large" | "xlarge">;

    /**
     * @default false
     */
    open?: boolean;
    onClose?: () => void;
    /**
     * Callback when clicking outside the dialog
     */
    onClickOutside?: (e: globalThis.MouseEvent) => void;
    /**
     * Enables scroll chaining
     * @link https://developer.mozilla.org/en-US/docs/Glossary/Scroll_chaining
     */
    enableScrollChaining?: boolean;
    /**
     * Given an array of focusable elements, return the initial focus target.
     * @param focusables - The focusable elements to pick from
     * @returns The initial focus target
     */
    initialFocusResolver?: (focusables: FocusableElement[]) => FocusableElement;
    /**
     * Sets the default DrillDown index when used in an uncontrolled way
     */
    defaultDrillDownIndex?: DrillDownContextProps["index"];
    /**
     * Callback to pass context to useDrillDown hook
     */
    onDrillDownContextChange?: (context: DrillDownContextProps) => void;
  };

/**
 * Dialog component for creating modal dialogs and overlays.
 *
 * Features:
 * - Modal dialog with backdrop overlay
 * - Controlled and uncontrolled open state
 * - Smooth open/close animations
 * - Keyboard navigation and focus management
 * - Escape key and click outside to close
 * - Multiple size variants (medium, large, fullscreen)
 * - Scroll chaining support
 * - Toast integration for notifications
 * - Compound component pattern with Header, Footer, Content, and CancelButton sub-components
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
 *   <Dialog.Header>Confirm Action</Dialog.Header>
 *   <Dialog.Content>Are you sure you want to proceed?</Dialog.Content>
 *   <Dialog.Footer>
 *     <Dialog.CancelButton>Cancel</Dialog.CancelButton>
 *     <Button appearance="primary">Confirm</Button>
 *   </Dialog.Footer>
 * </Dialog>
 */
export const Dialog = Object.assign(
  forwardRef<HTMLDialogElement, DialogProps>(function DialogInner(props, ref) {
    const {
      className,
      open = false,
      children,
      onClose,
      onKeyDown,
      fullScreen = false,
      disableCloseOnEscape = false,
      onClickOutside,
      disableCloseOnClickOutside = false,
      size,
      enableScrollChaining,
      onOpenAnimationStart,
      onOpenAnimationComplete,
      onCloseAnimationStart,
      onCloseAnimationComplete,
      initialFocusResolver,
      defaultDrillDownIndex,
      onDrillDownContextChange,
      ...rest
    } = props;

    const elRef = useRef<HTMLDialogElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const toasterRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);

    const [hasOpenPopover, setHasOpenPopover] =
      useState<DialogContextProps["hasOpenPopover"]>(false);

    const drillDownContextValue = useDrillDownContextState({
      defaultDrillDownIndex,
    });

    const id = useId();

    const { state, isOpenOrOpening } = useDialogTransitionStates(elRef, open, {
      onOpenAnimationStart: useCallback(() => {
        onOpenAnimationStart?.();
      }, [onOpenAnimationStart]),
      onOpenAnimationComplete: useCallback(() => {
        onOpenAnimationComplete?.();
      }, [onOpenAnimationComplete]),
      onCloseAnimationStart: useCallback(() => {
        onCloseAnimationStart?.();
      }, [onCloseAnimationStart]),
      onCloseAnimationComplete: useCallback(() => {
        onCloseAnimationComplete?.();
      }, [onCloseAnimationComplete]),
    });

    const previousOpenRef = useRef(open);

    useLayoutEffect(() => {
      if (open && !previousOpenRef.current) {
        drillDownContextValue.setHostTrigger?.(
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null,
        );
      }
    }, [open, drillDownContextValue]);

    useEffect(() => {
      previousOpenRef.current = open;
    }, [open]);

    useEffect(() => {
      if (state === "closing") {
        drillDownContextValue.onHostClose?.();
      }
    }, [state, drillDownContextValue]);

    useEffect(() => {
      if (state === "closed") {
        drillDownContextValue.onHostClosed?.();
      }
    }, [state, drillDownContextValue]);

    const drillDownContextProviderValue = useMemo(
      () => ({
        ...drillDownContextValue,
        hostIsInteractive: state !== "closing" && state !== "closed",
      }),
      [drillDownContextValue, state],
    );

    useLayoutEffect(() => {
      onDrillDownContextChange?.(drillDownContextProviderValue);
    }, [drillDownContextProviderValue]); // eslint-disable-line react-hooks/exhaustive-deps

    // DEPRECATED
    const initial = useRef(true);
    useEffect(() => {
      if (initial.current) {
        initial.current = false;
      }
    }, [id, isOpenOrOpening]);

    const classNames = cx([styles.dialog, drillDownStyles.dialog], className, {
      [styles["closing"]]: state === "closing",
      [styles["fullscreen"]]: fullScreen,
      ...(size && { [styles[size]]: true }),
    });

    const { focusables } = useKeyboardFocusables(contentRef, {
      observeChange: true,
    });

    useDialogScrollLock({
      open: isOpenOrOpening,
      id,
      enableScrollChaining,
      elementRef: elRef,
    });

    // Register/unregister with layer manager for proper stacking
    useEffect(() => {
      if (open) {
        registerLayer(id);
      } else {
        unregisterLayer(id);
      }

      // Cleanup when component unmounts
      return () => {
        unregisterLayer(id);
      };
    }, [open, id]);

    useOnClickOutside({
      onClickOutsideBegin: (e) => {
        if (!open) return;
        onClickOutside?.(e);
      },
      targets: [
        contentRef.current as HTMLElement,
        toasterRef.current as HTMLElement,
      ],
      onClickOutside: () => {
        if (disableCloseOnClickOutside || !open) return;
        onClose?.();
      },
      layerId: id,
    });

    const computeFocus = useCallback((focusables: FocusableElement[]) => {
      return (
        focusables.find(
          (focusable) => focusable.dataset.anv !== "dialog-cancel-button",
        ) || focusables[0]
      );
    }, []);

    useInitialFocus(elRef, {
      computeFocus: initialFocusResolver || computeFocus,
    });

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDialogElement>) => {
      onKeyDown?.(e);
      if (e.code === "Escape") {
        e.preventDefault();
        if (disableCloseOnEscape) return;
        if (hasOpenPopover) return;
        onClose?.();
        return;
      }

      if (e.code === "Tab") {
        if (e.shiftKey && e.target === focusables?.[0]) {
          e.preventDefault();
          (focusables[focusables.length - 1] as HTMLElement)?.focus();
          return;
        }
        if (e.target === focusables?.[focusables.length - 1] && !e.shiftKey) {
          e.preventDefault();
          focusables[0].focus();
          return;
        }
      }
    };

    return (
      // dialog element does seem to be interactive on MDN guideline
      <dialog
        className={classNames}
        data-anv="dialog"
        data-anv-scroll-chaining={enableScrollChaining}
        data-anv-transition-state={state}
        ref={useMergeRefs([ref, elRef])}
        onKeyDown={onKeyDownHandler}
        {...rest}
      >
        <ToastProvider isDialogToaster={open}>
          <ToasterPopover>
            <ToasterContent id="dialog-toaster" ref={toasterRef} />
          </ToasterPopover>
        </ToastProvider>

        <DrillDownContext.Provider value={drillDownContextProviderValue}>
          <DialogContext.Provider
            value={{
              close: () => onClose?.(),
              ref: elRef,
              hasOpenPopover,
              setHasOpenPopover,
              headerRef,
              state,
            }}
          >
            <section
              ref={contentRef}
              className={styles["container"]}
              data-open={open}
            >
              {children}
            </section>
          </DialogContext.Provider>
        </DrillDownContext.Provider>
      </dialog>
    );
  }),
  {
    /**
     * DialogHeader component for the header section of a Dialog.
     *
     * Features:
     * - Displays dialog title with headline typography
     * - Includes close button with proper accessibility
     * - Sticky positioning for long content
     * - Consistent spacing and styling
     * - Automatic focus management
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Dialog.Header>Confirm Delete</Dialog.Header>
     */
    Header: DialogHeader,
    /**
     * DialogFooter component for the footer section of a Dialog.
     *
     * Features:
     * - Container for dialog action buttons
     * - Optional sticky positioning for long content
     * - Consistent spacing and alignment
     * - Right-aligned button layout by default
     * - Flexible content area for custom layouts
     *
     * @example
     * <Dialog.Footer sticky>
     *   <Dialog.CancelButton>Cancel</Dialog.CancelButton>
     *   <Button appearance="primary">Save</Button>
     * </Dialog.Footer>
     */
    Footer: DialogFooter,
    /**
     * DialogContent component for the main content area of a Dialog.
     *
     * Features:
     * - Main content container for dialog body
     * - Flexible height with overflow handling
     * - Consistent padding and spacing
     * - Responsive design
     * - Proper content scrolling for long content
     *
     * @example
     * <Dialog.Content>
     *   <p>This is the main content of the dialog.</p>
     * </Dialog.Content>
     */
    Content: DialogContent,
    /**
     * DialogCancelButton component for cancel/close actions in a Dialog.
     *
     * Features:
     * - Button that automatically closes the dialog when clicked
     * - Extends all Button component props and functionality
     * - Proper accessibility with close dialog semantics
     * - Automatic focus management
     * - Consistent styling with other dialog elements
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Dialog.CancelButton appearance="ghost">Cancel</Dialog.CancelButton>
     */
    CancelButton: DialogCancelButton,
  },
);
Dialog.displayName = "Dialog";
export const DialogElement = Dialog;
